import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
// #region --------------- Contact React Query hooks ----------------------

import {
  createMatterTeammateV1,
  deleteMatterTeammateV1,
  retrieveMatterTeammatesV1,
  updateMatterTeammateV1,
  type MatterSettings,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  type APIError,
  type APIResponse,
  type MatterTeammate,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveMatterTeammatesQueryOptions = (
  data: Parameters<typeof retrieveMatterTeammatesV1>[0],
) => {
  return {
    queryKey: [queryKeys.matterTeammates, data.id],
    queryFn: async () => retrieveMatterTeammatesV1(data),
    enabled: !!data.id,
  };
};

export const useCreateMatterTeammate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTeammateV1>[0]) =>
      createMatterTeammateV1(data),
    onMutate: (data) => {
      // Optimistic update
      const matterRoles = (
        queryClient.getQueryData([
          queryKeys.matterSettings,
        ]) as APIResponse<MatterSettings>
      ).data.roles;
      queryClient.setQueryData(
        [queryKeys.matterTeammates, data.teamId],
        (oldData: APIResponse<MatterTeammate[]> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: [
              ...oldData.data,
              {
                ...data,
                userId: data.user.userId,
                roleLabel:
                  matterRoles.find((role) => role.id === data.roleId)?.label ??
                  "",
              },
            ],
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.matterTeammates,
          data.teamId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterTeammates, variables.teamId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter teammate");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterTeammates, variables.teamId],
      });
    },
    onSuccess: () => {
      toast.success("Matter team created successfully");
    },
  });
};

export const useUpdateMatterTeammate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTeammateV1>[0]) =>
      updateMatterTeammateV1(data),
    onMutate: (data) => {
      // Optimistic update
      const matterRoles = (
        queryClient.getQueryData([
          queryKeys.matterSettings,
        ]) as APIResponse<MatterSettings>
      ).data.roles;
      queryClient.setQueryData(
        [queryKeys.matterTeammates, data.teamId],
        (oldData: APIResponse<MatterTeammate[]> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: oldData.data.map((teammate) =>
              teammate.id === data.id
                ? {
                    ...teammate,
                    ...data,
                    roleLabel:
                      matterRoles.find((role) => role.id === data.roleId)
                        ?.label ?? teammate.roleLabel,
                  }
                : teammate,
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.matterTeammates,
          data.teamId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterTeammates, variables.teamId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter teammate");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterTeammates, variables.teamId],
      });
    },
    onSuccess: () => {
      toast.success("Matter teammate updated successfully");
    },
  });
};

export const useDeleteMatterTeammate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTeammateV1>[0]) =>
      deleteMatterTeammateV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterTeammates, data.teamId],
        (oldData: APIResponse<MatterTeammate[]> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: oldData.data.filter((teammate) => teammate.id !== data.id),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.matterTeammates,
          data.teamId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterTeammates, variables.teamId],
          context.previousData,
        );
      }
      toast.error("Error deleting matter teammate");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterTeammates, variables.teamId],
      });
    },
    onSuccess: () => {
      toast.success("Matter teammate deleted successfully");
    },
  });
};
